import React from 'react';
import { Typography, List, ListItem, ListItemText } from '@mui/material';
import HelpSidebar from './HelpSidebar';

interface OrganisationExpensesHelpSidebarProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export const OrganisationExpensesHelpSidebar: React.FC<OrganisationExpensesHelpSidebarProps> = ({
  isExpanded,
  onToggle,
}) => (
  <HelpSidebar
    isExpanded={isExpanded}
    onToggle={onToggle}
    title="Organisation Expenses Help"
  >
    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
      This page shows organisation-wide expense claims for reporting and visibility. Use the filters to narrow by department, status, or date range.
    </Typography>
    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
      <strong>Access:</strong> Organisation Expenses is available to <strong>Administrators</strong>, <strong>Senior Managers</strong>, and users in a <strong>Finance</strong> department (for example, "Finance Team"). It is also subject to the environment feature gate and is hidden in production while sign-off is pending.
    </Typography>

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      FILTERS
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Department"
          secondary="Show only claims from employees in the selected department."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Status & dates"
          secondary="Filter by claim status (e.g. Pending, Approved) and claim date range. The date range defaults to the last 90 days; results update when you change filters. Claim dates are always shown in the organisation time zone; times follow Times on screen on your Profile."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      CHARTS
    </Typography>
    <Typography variant="body2" sx={{ mb: 2 }}>
      Expand the <strong>Charts</strong> section to see spend by category, status, department (top 10), approved by manager (bar and table), spend over time (by day when the selected range is 45 days or less, otherwise by month), and top requesters (top 10). All amounts are shown in £. Charts use the same filters as the table.
    </Typography>

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      READ-ONLY VIEW
    </Typography>
    <Typography variant="body2" sx={{ mb: 2 }}>
      Approve or reject claims from <strong>Team Expenses</strong> (as the requester&apos;s manager). Use <strong>View</strong> here to open a claim for details only.
    </Typography>
  </HelpSidebar>
);

export default OrganisationExpensesHelpSidebar;
